import type { ReactNode } from "react";
import React from "react";

import type { TextSize } from "constants/WidgetConstants";
import { countOccurrences } from "workers/Evaluation/helpers";

import { ValidationTypes } from "constants/WidgetValidation";
import type { DerivedPropertiesMap } from "utils/WidgetFactory";

import WidgetStyleContainer from "components/designSystems/appsmith/WidgetStyleContainer";
import type { Color } from "constants/Colors";
import type { Stylesheet } from "entities/AppTheming";
import { pick } from "lodash";
import { AutocompleteDataType } from "utils/autocomplete/AutocompleteDataType";
import type { WidgetProps, WidgetState } from "widgets/BaseWidget";
import BaseWidget from "widgets/BaseWidget";
import type { ContainerStyle } from "widgets/ContainerWidget/component";
import type { TextAlign } from "../component";
import TextComponent from "../component";
import { OverflowTypes } from "../constants";
import { DefaultAutocompleteDefinitions } from "widgets/WidgetUtils";
import type { AutocompletionDefinitions } from "widgets/constants";
import { EventType } from "constants/AppsmithActionConstants/ActionConstants";
// import {DateFormatOptions} from "../../DatePickerWidget2/widget/constants";
const MAX_HTML_PARSING_LENGTH = 1000;
class TextWidget extends BaseWidget<TextWidgetProps, WidgetState> {
  static getPropertyPaneEventConfig() {
    return super.getWidgetEvents('DigitalCardWidget');
  }
  static getPropertyPaneContentConfig() {
    return [
      {
        sectionName: "基本",
        children: [{
          propertyName: "label",
          helpText: "Sets the text of the widget",
          label: "标签",
          controlType: "INPUT_TEXT",
          placeholderText: "",
          isBindProperty: true,
          isTriggerProperty: false,
          validation: {
            type: ValidationTypes.TEXT,
            params: { limitLineBreaks: true },
          },
        }]
      },
      {
        sectionName: "数据",
        children: [
          {
            propertyName: "isDecimal",
            helpText: "Controls the visibility of the widget",
            label: "是否为小数",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {

            propertyName: "numDecimal",
            label: "小数位数",
            controlType: "NUMERIC_INPUT",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            min: 1,
            max:15,
            defaultValue: 1,
            hidden: (props: any) => props.isDecimal == false,
            validation: {
              type: ValidationTypes.NUMBER,
              params: {
                natural: true,
              },
            },
          },
          {
            helpText:
              "Enter value for border width which can also use as margin",
            propertyName: "defaultCount",
            label: "初始值",
            placeholderText: "Enter value in px",
            controlType: "INPUT_TEXT",
            isBindProperty: true,
            defaultValue: 0,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.NUMBER },
          },
          {
            helpText:
              "Enter value for border width which can also use as margin",
            propertyName: "maxCount",
            label: "目标值",
            placeholderText: "Enter value in px",
            controlType: "INPUT_TEXT",
            isBindProperty: true,
            defaultValue: 10000,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.NUMBER },
          },
          {
            propertyName: "isFixed",
            helpText: "Controls the visibility of the widget",
            label: "位数是否固定",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            helpText:
              "Enter value for border width which can also use as margin",
            propertyName: "numFixed",
            label: "固定多少位",
            placeholderText: "Enter value in px",
            controlType: "INPUT_TEXT",
            isBindProperty: true,
            defaultValue: 5,
            isTriggerProperty: false,
            hidden: (props: any) => props.isFixed == false,
            validation: { type: ValidationTypes.NUMBER },
          },
        ],
      },
      {
        sectionName: "动效",
        children: [
          {
            helpText:
              "Enter value for border width which can also use as margin",
            propertyName: "time",
            label: "时长(毫秒)",
            placeholderText: "Enter value in px",
            controlType: "INPUT_TEXT",
            isBindProperty: true,
            defaultValue: 5000,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.NUMBER },
          },
          {
            helpText:
              "Enter value for border width which can also use as margin",
            propertyName: "step",
            label: "帧数",
            controlType: "NUMERIC_INPUT",
            isBindProperty: true,
            defaultValue: 50,
            min:1,
            isTriggerProperty: true,
            validation: {
              type: ValidationTypes.NUMBER,
              params: {
                natural: true,
              },
            },
          },
        ]
      },
      {
        sectionName: "一般",
        children: [
          {
            propertyName: "isVisible",
            helpText: "Controls the visibility of the widget",
            label: "是否可见",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          }
        ],
      },
    ];
  }

  static getStylesheetConfig(): Stylesheet {
    return {
      fontFamily: "{{appsmith.theme.fontFamily.appFont}}",
      //borderRadius: "{{appsmith.theme.borderRadius.appBorderRadius}}",
      //textColor: "{{appsmith.theme.colors.textColor}}",
    };
  }

  static getPropertyPaneStyleConfig() {
    return [
      {
        sectionName: "文字",
        children: [
          {
            propertyName: "fontFamily",
            label: "字体",
            helpText: "Controls the font family being used",
            controlType: "DROP_DOWN",
            options: [
              {
                label: "System Default",
                value: "System Default",
              },
              {
                label: "Nunito Sans",
                value: "Nunito Sans",
              },
              {
                label: "Poppins",
                value: "Poppins",
              },
              {
                label: "Inter",
                value: "Inter",
              },
              {
                label: "Montserrat",
                value: "Montserrat",
              },
              {
                label: "Noto Sans",
                value: "Noto Sans",
              },
              {
                label: "Open Sans",
                value: "Open Sans",
              },
              {
                label: "Roboto",
                value: "Roboto",
              },
              {
                label: "Rubik",
                value: "Rubik",
              },
              {
                label: "Ubuntu",
                value: "Ubuntu",
              },
            ],
            defaultValue: "System Default",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.TEXT,
            },
          },
          {
            propertyName: "fontSize",
            label: "字体大小",
            helpText: "Controls the size of the font used",
            controlType: "DROP_DOWN",
            defaultValue: "1rem",
            options: [
              {
                label: "S",
                value: "0.875rem",
                subText: "0.875rem",
              },
              {
                label: "M",
                value: "1rem",
                subText: "1rem",
              },
              {
                label: "L",
                value: "1.25rem",
                subText: "1.25rem",
              },
              {
                label: "XL",
                value: "1.875rem",
                subText: "1.875rem",
              },
              {
                label: "XXL",
                value: "3rem",
                subText: "3rem",
              },
              {
                label: "3XL",
                value: "3.75rem",
                subText: "3.75rem",
              },
            ],
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.TEXT,
            },
          },
          // {
          //   propertyName: "lineHeight",
          //   helpText: "Changes the width of the button",
          //   label: "行高",
          //   controlType: "INPUT_TEXT",
          //   isJSConvertible: false,
          //   isBindProperty: true,
          //   // isTriggerProperty: false,
          //   validation: { type: ValidationTypes.NUMBER },
          // },

          {
            propertyName: "fontStyle",
            label: "加粗&斜体",
            helpText: "Controls the font emphasis of the text displayed",
            controlType: "BUTTON_GROUP",
            options: [
              {
                icon: "text-bold",
                value: "BOLD",
              },
              {
                icon: "text-italic",
                value: "ITALIC",
              },
            ],
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          },
        ],
      },
      {
        sectionName: "卡片",
        children: [
          {
            propertyName: "widthPadding",
            label: "间距",
            controlType: "NUMERIC_INPUT",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            min: 0,
            defaultValue: 5,
            validation: {
              type: ValidationTypes.NUMBER,
              params: {
                natural: true,
              },
            },
          },
          {
            propertyName: "widthBorderRadius",
            label: "圆角",
            controlType: "NUMERIC_INPUT",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            min: 0,
            defaultValue: 3,
            validation: {
              type: ValidationTypes.NUMBER,
              params: {
                natural: true,
              },
            },
          },
        ],
      },
      {
        sectionName: "颜色",
        children: [
          {
            propertyName: "textColor",
            label: "字体颜色",
            helpText: "Controls the color of the text displayed",
            controlType: "COLOR_PICKER",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.TEXT,
              params: {
                regex: /^(?![<|{{]).+/,
              },
            },
          },
          {
            propertyName: "backgroundColor",
            label: "卡片颜色",
            helpText: "Background color of the text added",
            controlType: "COLOR_PICKER",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.TEXT,
              params: {
                regex: /^((?![<|{{]).+){0,1}/,
                expected: {
                  type: "string (HTML color name or HEX value)",
                  example: `red | #9C0D38`,
                  autocompleteDataType: AutocompleteDataType.STRING,
                },
              },
            },
          },
          // {
          //   helpText: "Use a html color name, HEX, RGB or RGBA value",
          //   placeholderText: "#FFFFFF / Gray / rgb(255, 99, 71)",
          //   propertyName: "borderColor",
          //   label: "边框颜色",
          //   controlType: "COLOR_PICKER",
          //   isBindProperty: true,
          //   isTriggerProperty: false,
          //   validation: { type: ValidationTypes.TEXT },
          // },
        ],
      },
      {
        sectionName: "边框&阴影",
        children: [
          {

            propertyName: "borderWidth",
            label: "边框宽度",
            controlType: "NUMERIC_INPUT",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            min: 0,
            defaultValue: 1,
            validation: {
              type: ValidationTypes.NUMBER,
              params: {
                natural: true,
              },
            },
          },
          {
            propertyName: "borderRadius",
            label: "边框圆角",
            helpText: "Rounds the corners of the icon button's outer border edge",
            controlType: "BORDER_RADIUS_OPTIONS",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          },
          {
            propertyName: "boxShadow",
            label: "阴影",
            helpText:
              "Enables you to cast a drop shadow from the frame of the widget",
            controlType: "BOX_SHADOW_OPTIONS",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          },
        ],
      },
    ];
  }

  static getDerivedPropertiesMap(): DerivedPropertiesMap {
    return {
      value: `{{ this.value }}`,
      getValue: `{{ this.value }}`,
    };
  }
  static getMetaPropertiesMap(): Record<string, any> {
    return {
      value: ''
    };
  }

  handleActionResult = (result: any) => { };


  getPageView() {

    return (
      <WidgetStyleContainer
        accentColor="transparent"
        className="t--text-widget-container"
        {...pick(this.props, [
          "widgetId",
          "containerStyle",
          "borderColor",
          "borderWidth",
        ])}
      >
        <TextComponent
          key={this.props.widgetId}
          widgetId={this.props.widgetId}
          isDecimal={this.props.isDecimal}
          numDecimal={this.props.numDecimal}
          defaultCount={this.props.defaultCount}
          maxCount={this.props.maxCount}
          isFixed={this.props.isFixed}
          numFixed={this.props.numFixed}
          time={this.props.time}
          step={this.props.step}
          fontFamily={this.props.fontFamily}
          fontSize={this.props.fontSize}
          fontStyle={this.props.fontStyle}
          widthPadding={this.props.widthPadding}
          widthBorderRadius={this.props.widthBorderRadius}
          textColor={this.props.textColor}
          backgroundColor={this.props.backgroundColor}
          borderWidth={this.props.borderWidth}
          borderRadius={this.props.borderRadius}
          boxShadow={this.props.boxShadow}
          value={this.props.value} 
        />
      </WidgetStyleContainer>
    );
  }



  static getAutocompleteDefinitions(): AutocompletionDefinitions {
    return {
      "!doc":
        "‌Text widget is used to display textual information. Whether you want to display a paragraph or information or add a heading to a container, a text widget makes it easy to style and display text",
      "!url": "https://docs.appsmith.com/widget-reference/text",
      isVisible: DefaultAutocompleteDefinitions.isVisible,
      text: "string",
    };
  }

  static getWidgetType() {
    return "DIGITALCARD_WIDGET";
  }
}

export interface TextStyles {

  isDecimal?: number,
  numDecimal?: number,
  defaultCount?: number,
  maxCount?: number,
  isFixed?: Boolean;
  numFixed?: number,
  time?: number,
  step?: number,
  fontFamily?: string;
  fontSize?: string;
  fontStyle?: string;
  widthPadding?: number,
  widthBorderRadius?: string;
  textColor?: string;
  backgroundColor?: string;
  borderWidth?: string;
  borderRadius?: string;
  boxShadow?: string;
}

export interface TextWidgetProps extends WidgetProps, TextStyles {

  isDecimal?: boolean,
  numDecimal?: number,
  defaultCount?: number,
  maxCount?: number,
  isFixed?: Boolean;
  numFixed?: number,
  time?: number,
  step?: number,
  fontFamily?: string;
  fontSize?: string;
  fontStyle?: string;
  widthPadding?: number,
  widthBorderRadius?: string;
  textColor?: string;
  backgroundColor?: string;
  borderWidth?: string;
  borderRadius?: string;
  boxShadow?: string;
  value?:any
}

export default TextWidget;
